import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import {
  TransformedHeatmapResponse,
  useGetGIHeatmapQuery,
  useGetVIHeatmapQuery,
} from '@customer/store/apis/heatmapsApi';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useCallback } from 'react';
import { MapBoundsZoom } from './LocationFilter';
import {
  getLocationFilterEntityId,
  setLocationFilter,
  useLocationFilterParams,
} from '@customer/store/slices/locationFilterSlice';
import { LocationParams } from '@customer/store/apis/helpers';
import { usePerformanceTimeRangeFilters } from '@customer/store/slices/globalSlice';

const INITIAL_LOCATION_PARAMS: Partial<LocationParams> = {
  startLat: undefined,
  startLon: undefined,
  endLat: undefined,
  endLon: undefined,
  zoom: undefined,
};

interface UseLocationFilterControllerProps {
  filterGroup:
    | FilterGroup.AssetGI
    | FilterGroup.AssetVI
    | FilterGroup.PerformanceGI
    | FilterGroup.PerformanceVI;
  useHeatmapQuery: typeof useGetVIHeatmapQuery | typeof useGetGIHeatmapQuery;
  includeAssetId?: boolean;
  assetId?: string;
}

export type UpdateLocationParamsFunc = (mapBoundsZoom: MapBoundsZoom | null) => void;

export interface LocationFilterProps extends Partial<TransformedHeatmapResponse> {
  isFetching: boolean;
  updateSearchParams: UpdateLocationParamsFunc;
}

export const useLocationFilterController = ({
  filterGroup,
  useHeatmapQuery,
  includeAssetId = false,
  assetId,
}: UseLocationFilterControllerProps): LocationFilterProps => {
  const dispatch = useAppDispatch();

  const siteId = useAppSelector(state => state.auth.siteId);

  const filters = useFilters(filterGroup);

  const timeRangeFilters = usePerformanceTimeRangeFilters();

  const lfEntityId = getLocationFilterEntityId({
    filterGroup,
    includeAssetId,
    assetId,
  });

  const updateSearchParams: UpdateLocationParamsFunc = useCallback(
    mapBoundsZoom => {
      if (lfEntityId) {
        // Remove/reset the location params by default.
        let newLocationParams: Partial<LocationParams> = INITIAL_LOCATION_PARAMS;

        if (mapBoundsZoom) {
          // Update the location params with the current map view details.
          newLocationParams = {
            startLat: mapBoundsZoom.northLat,
            startLon: mapBoundsZoom.westLon,
            endLat: mapBoundsZoom.southLat,
            endLon: mapBoundsZoom.eastLon,
            zoom: mapBoundsZoom.zoom,
          };
        }

        // Update redux store with new search params
        dispatch(setLocationFilter({ ...newLocationParams, id: lfEntityId }));
      }
    },
    [dispatch, lfEntityId],
  );

  const locationFilters = useLocationFilterParams({
    filterGroup,
    includeAssetId,
    assetId,
  });

  // Use this value to skip queries if assetId is expected but undefined (loading or not provided).
  const assetIdIsMissing = includeAssetId && typeof assetId === 'undefined';

  const { data, isFetching } = useHeatmapQuery(
    {
      query: {
        ...filters,
        ...locationFilters,
        ...timeRangeFilters,
        assetIds: includeAssetId ? assetId : undefined,
      },
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: typeof siteId === 'undefined' || assetIdIsMissing || lfEntityId === 'undefined',
    },
  );

  return {
    ...data,
    isFetching,
    updateSearchParams,
  };
};

export default useLocationFilterController;
